import { forwardRef, useEffect, useRef, useState } from 'react';

import { Box, Fade, Flex, Grid, Img, Portal } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Card, Contest, ContestParticipant } from 'models';

import CardPlaceholder from 'components/builder/CardPlaceholder';
import Squad from 'components/builder/Squad/Squad';

import { ReactComponent as NoneIcon } from 'icons/none.svg';

interface ContestHistoryItemProps {
  participation: ContestParticipant;
}

const ContestHistoryItem = forwardRef<HTMLDivElement, ContestHistoryItemProps>(({ participation }, ref) => {
  const cardPlaceholderWrapperRef = useRef<HTMLDivElement>(null);
  const [cardOffset, setCardOffset] = useState(0);
  const [showParticipationDetail, setShowParticipationDetail] = useState(false);
  const formattedDate = dayjs(participation.contest?.startsAt).format('M/DD');
  const { place, contest, totalPoints, booster, cards, updated, user } = participation;
  const startCards = cards.cardsWithStatus.filter((card) => card.status === 'start');
  const benchCard = cards.cardsWithStatus.find((card) => card.status === 'bench');
  const cardsWithStatus = cards.cardsWithStatus;

  const createEmptyCard = (index: number): Card & { isEmpty: boolean } => ({
    id: -1 - index,
    status: index < 3 ? 'start' : 'bench',
    playerSrId: `empty-${index}`,
    playerFirstName: '',
    playerLastName: '',
    playerName: '',
    playerAbbrName: '',
    playerPrimaryPosition: '',
    homeTeamSrId: '',
    awayTeamSrId: '',
    isHomeTeam: false,
    modifierPoints: 0,
    boxScore: null,
    modifier: null,
    gameSrId: '',
    playerInjuries: false,
    headshotThumbnails: null,
    nakedScorePoints: 0,
    totalScorePoints: 0,
    nakedAttributePoints: 0,
    totalAttributePoints: 0,
    totalPoints: 0,
    isEmpty: true,
  });

  const [lineup, setLineup] = useState<(Card & { isEmpty: boolean })[]>(() =>
    Array.from({ length: 4 }, (_, index) => createEmptyCard(index))
  );

  useEffect(() => {
    if (cardPlaceholderWrapperRef.current) {
      const offsetDiff = cardPlaceholderWrapperRef.current.offsetWidth / 5;
      setCardOffset(
        cardPlaceholderWrapperRef.current.offsetWidth / 4 > 64
          ? offsetDiff - 1.8
          : offsetDiff - (64 - cardPlaceholderWrapperRef.current.offsetWidth / 4)
      );
    }
  }, [cardPlaceholderWrapperRef.current]);

  useEffect(() => {
    const newLineup = Array.from({ length: 4 }, (_, index) => {
      const participationCard = participation.cards.cardsWithStatus
        .filter((card) => card.status === 'start' || card.status === 'bench')
        .sort((a, b) => {
          if (a.status === 'start' && b.status === 'bench') return -1;
          if (a.status === 'bench' && b.status === 'start') return 1;
          return 0;
        })[index];
      if (participationCard) {
        return { ...participationCard, isEmpty: false };
      }
      return createEmptyCard(index);
    });
    setLineup(newLineup);
  }, [participation]);

  if (showParticipationDetail) {
    const date = dayjs(updated).tz('America/Los_Angeles');
    const dateStr = date.format('MMM D');

    return (
      <Portal>
        <Squad
          participation={participation}
          cards={cards.cardsWithStatus}
          contest={contest}
          pos="fixed"
          top="0"
          bottom="0"
          left="0"
          right="0"
          bg="var(--chakra-colors-bg)"
          onClose={() => setShowParticipationDetail(false)}
          title={`${user.username}`}
          description={`Start Bench Cut · ${dateStr}`}
          overflow="auto"
          detailed
          zIndex="100"
          contestStatus={'completed'}
          className="portal"
        />
      </Portal>
    );
  }

  return (
    <Fade in>
      <Flex
        w="100%"
        borderBottom="0.5px solid #FFFFFF4D"
        onClick={() => setShowParticipationDetail(true)}
        cursor="pointer"
        transition="all 0.2s ease-in-out"
        _active={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
      >
        <Grid
          ref={ref}
          w="100%"
          maxW="375px"
          mx="auto"
          gridTemplateRows="36px 1fr"
          gridTemplateColumns="60px auto"
          p="4px 12px 16px"
        >
          <Flex
            fontSize="14px"
            fontWeight="800"
            lineHeight="1.2"
            textAlign="left"
            alignItems="center"
            color={place === 1 ? 'rgba(251, 188, 5, 1)' : 'white'}
          >
            {formattedDate}
          </Flex>
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Box
              fontSize="14px"
              fontWeight="800"
              lineHeight="1.2"
              textAlign="left"
              textTransform="uppercase"
              color={place === 1 ? 'rgba(251, 188, 5, 1)' : 'white'}
            >
              {place ? `#${place}` : '--'} @ {contest?.league}
            </Box>
            <Flex flexDirection="row" gap="4px" alignItems="center">
              <Box fontSize="14px" fontWeight="800" lineHeight="1.2">
                {contest?.status !== 'completed' ? 'TBA' : totalPoints}
              </Box>
              {/* <Img w="16px" h="16" objectFit="contain" src="/coin.png" /> */}
            </Flex>
          </Flex>
          <Box />
          <Flex
            ref={cardPlaceholderWrapperRef}
            justifyContent="flex-end"
            flexDirection="row"
            w="100%"
            h="64px"
            flexWrap="nowrap"
            pos="relative"
            gap="5px"
          >
            {lineup.map((card) => (
              <CardPlaceholder
                key={card.id}
                card={card.isEmpty ? undefined : card}
                type={card.status as 'start' | 'bench'}
              />
            ))}
            <CardPlaceholder booster={booster ?? undefined} type="booster" />
          </Flex>
        </Grid>
      </Flex>
    </Fade>
  );
});

export default ContestHistoryItem;
